import React from 'react'
import Pt from 'prop-types'
import Ipt from 'react-immutable-proptypes'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  HelpBlock,
  Panel,
  Row,
} from 'react-bootstrap'

import {
  FormErrors,
  FormField,
  FormGroup,
} from 'internal-components/src/components/Form'
import Icon from 'internal-components/src/components/Icon'

import {
  OfferFilterRecord,
  SignupWithExternalsRecord,
} from 'internal-components/src/records'

import AccountDetails from './AccountDetails'
import AddressEntry from './AddressEntry'
import CompanyDetails from './CompanyDetails'
import MspClientSelector from './MspClientSelector'
import MspClientUserSelector from './MspClientUserSelector'
import OfferSelector from '../../components/OfferSelector'
import ServiceDetail from './ServiceDetail'
import ServiceOptions, { shouldShowOptions, handleSubmitOptions } from './ServiceDetail/ServiceOptions'

export default function MspSignup({
  agreeTerms,
  emailChecking,
  emailRegistered,
  errors,
  filter,
  selectClient,
  selectOffer,
  selectUser,
  setAbn,
  setAddress,
  setAddressLocId,
  setChallengePhrase,
  setCompanyName,
  setCompanySize,
  setContact,
  setData,
  setDateOfBirth,
  setEmail,
  setEmailConfirm,
  setExtraOfferId,
  setFirstName,
  setLastName,
  setPreferredAppointmentDate,
  setPreferredAppointmentTime,
  setServicePassword,
  setSiteContactFirstName,
  setSiteContactLastName,
  setSiteContactNumber,
  setUserPassword,
  signupEditMeta,
  signupWithExternals,
  submit,
  updateFilter,
  working,
}) {
  const showAccountDetails = !!signupWithExternals.offer
  const showServiceDetails = showAccountDetails
  const disableComplete = !showAccountDetails || emailRegistered

  const handleSubmit = e => {
    e.preventDefault();

    handleSubmitOptions(signupWithExternals, signupEditMeta, setData, setExtraOfferId);

    submit();
  }

  return (
    <form onSubmit={handleSubmit}>
      <h4>Service Location</h4>

      <AddressEntry
        address={signupWithExternals.signup.address || ''}
        continueButton={false}
        errors={errors}
        placeholder="Enter the address for the new service"
        setAddress={setAddress}
        setAddressLocId={setAddressLocId}
      >
        <HelpBlock>For virtual services, leave this address blank.</HelpBlock>
      </AddressEntry>

      <hr />

      <h4>Plan Selection</h4>

      <OfferSelector
        addressSupplied={!!signupWithExternals.signup.address}
        currentOffer={signupWithExternals.offer}
        filter={filter}
        hideAddressButton
        selectOffer={selectOffer}
        updateFilter={updateFilter}
      />

      <hr />

      {showServiceDetails && (
        <div>
          <ServiceDetail
            errors={errors}
            product={signupWithExternals.product}
            setData={setData}
            setServicePassword={setServicePassword}
            signup={signupWithExternals.signup}
          />

          <hr />
        </div>
      )}

      {showServiceDetails && shouldShowOptions(signupWithExternals.product, signupWithExternals.offer, signupWithExternals.signup) && <Panel>
        <Panel.Body>
          <ServiceOptions />
        </Panel.Body>
      </Panel>}

      {showServiceDetails &&
        signupWithExternals.product.addressRequired && (
          <div>
            <h4>Site Contact</h4>

            <p>
              As this service potentially requires a site visit, we need the
              details of someone who will be present at the site for the
              installation.
            </p>

            <p>
              If you do not enter these details, the end user who finalises this
              signup will be required to provide them.
            </p>

            <FormField
              bsSize="large"
              controlId="siteContactFirstName"
              controlLabel="Contact First Name"
              errors={errors}
              onChange={e => setSiteContactFirstName(e.target.value)}
              value={signupWithExternals.signup.get('siteContactFirstName') || ''}
            />

            <FormField
              bsSize="large"
              controlId="siteContactLastName"
              controlLabel="Contact Last Name"
              errors={errors}
              onChange={e => setSiteContactLastName(e.target.value)}
              value={signupWithExternals.signup.get('siteContactLastName') || ''}
            />

            <FormField
              bsSize="large"
              controlId="siteContactNumber"
              controlLabel="Contact Number"
              errors={errors}
              onChange={e => setSiteContactNumber(e.target.value)}
              value={signupWithExternals.signup.get('siteContactNumber') || ''}
            />

            <hr />
          </div>
        )}

      {showAccountDetails && (
        <div>
          <h4>Client Details</h4>

          <Row>
            <Col md={4} mdPush={8}>
              <Panel>
                <Panel.Body>
                  <p>
                    Search for an existing Client that has authorised you as a
                    partner or create provide new Client details below.
                  </p>
                </Panel.Body>
              </Panel>
            </Col>

            <Col md={8} mdPull={4}>
              <MspClientSelector
                partner={signupWithExternals.partner}
                selectClient={selectClient}
                selectedClientId={signupWithExternals.signup.clientId}
              />
            </Col>
          </Row>

          {!!signupWithExternals.client && (
            <div>
              <h4>Authorised User Selection</h4>

              <Row>
                <Col md={8}>
                  <MspClientUserSelector
                    client={signupWithExternals.client}
                    selectedUser={signupWithExternals.signup.user}
                    selectUser={selectUser}
                  />

                  {errors.has('user') && (
                    <div className="has-error">
                      <FormErrors key="user" errors={errors.get('user')} />
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )}

          {!signupWithExternals.signup.clientId && (
            <div>
              <h4>Client Details - Business</h4>

              <Row>
                <Col md={4} mdPush={8}>
                  <Panel>
                    <Panel.Body>
                      <p>
                        If this signup is for a business, enter its name, ABN, and
                        size here. For a personal account, leave these fields blank.
                      </p>
                    </Panel.Body>
                  </Panel>
                </Col>

                <Col md={8} mdPull={4}>
                  <CompanyDetails
                    errors={errors}
                    setAbn={setAbn}
                    setCompanyName={setCompanyName}
                    setCompanySize={setCompanySize}
                    signup={signupWithExternals.signup}
                  />
                </Col>
              </Row>
            </div>
          )}

          {!signupWithExternals.signup.clientId && (
            <div>
              <h4>Personal Details</h4>

              <Row>
                <Col md={4} mdPush={8}>
                  <Panel>
                    <Panel.Body>
                      <p>
                        Enter the details of the primary individual who we will
                        communicate with regarding this signup.
                      </p>

                      <p>
                        The customer has an opportunity to change this
                        information before submitting the signup.
                      </p>
                    </Panel.Body>
                  </Panel>
                </Col>

                <Col md={8} mdPull={4}>
                  <AccountDetails
                    authenticate
                    emailChecking={emailChecking}
                    emailRegistered={emailRegistered}
                    errors={errors}
                    privateDetails={false}
                    renderRegistered={() => (
                      <Alert bsStyle="warning" style={{ marginTop: '0.5em' }}>
                        <p>
                          <Icon name="exclamation-triangle" /> This email
                          address is already registered with us. To continue you
                          will need to contact the individual and provide them
                          with the partner authorisation URL.
                        </p>
                      </Alert>
                    )}
                    setChallengePhrase={setChallengePhrase}
                    setContact={setContact}
                    setDateOfBirth={setDateOfBirth}
                    setEmail={setEmail}
                    setEmailConfirm={setEmailConfirm}
                    setFirstName={setFirstName}
                    setLastName={setLastName}
                    setUserPassword={setUserPassword}
                    signup={signupWithExternals.signup}
                  />
                </Col>
              </Row>
            </div>
          )}

          <hr />
        </div>
      )}

      <Row>
        <Col md={12}>
          <FormGroup
            bsSize="large"
            controlId="mspAgree"
            errors={errors}
            errorKey="terms"
          >
            <Checkbox
              checked={!!signupWithExternals.signup.get('agreeTerms')}
              disabled={disableComplete}
              onChange={e => agreeTerms(e.target.checked)}
            >
              I agree to the Infinite Networks Standard Terms and Conditions
            </Checkbox>

            <HelpBlock>
              Our Standard Terms and Conditions are available{' '}
              <a href="https://www.infinite.net.au/policies-agreements/">
                here
              </a>
              .
            </HelpBlock>
          </FormGroup>

          <Button bsSize="large" type="submit" disabled={disableComplete}>
            Submit Signup <Icon name="thumbs-up" spin={working} />
          </Button>
        </Col>
      </Row>
    </form>
  )
}

MspSignup.propTypes = {
  agreeTerms: Pt.func.isRequired,
  emailChecking: Pt.bool,
  emailRegistered: Pt.bool,
  errors: Ipt.map.isRequired,
  filter: Pt.instanceOf(OfferFilterRecord).isRequired,
  selectClient: Pt.func.isRequired,
  selectOffer: Pt.func.isRequired,
  selectUser: Pt.func.isRequired,
  setAbn: Pt.func.isRequired,
  setAddress: Pt.func.isRequired,
  setAddressLocId: Pt.func.isRequired,
  setChallengePhrase: Pt.func.isRequired,
  setCompanyName: Pt.func.isRequired,
  setCompanySize: Pt.func.isRequired,
  setContact: Pt.func.isRequired,
  setData: Pt.func.isRequired,
  setDateOfBirth: Pt.func.isRequired,
  setEmail: Pt.func.isRequired,
  setEmailConfirm: Pt.func.isRequired,
  setExtraOfferId: Pt.func.isRequired,
  setFirstName: Pt.func.isRequired,
  setLastName: Pt.func.isRequired,
  setServicePassword: Pt.func.isRequired,
  setSiteContactFirstName: Pt.func.isRequired,
  setSiteContactLastName: Pt.func.isRequired,
  setSiteContactNumber: Pt.func.isRequired,
  setUserPassword: Pt.func.isRequired,
  signupWithExternals: Pt.instanceOf(SignupWithExternalsRecord).isRequired,
  signupEditMeta: Ipt.map.isRequired,
  submit: Pt.func.isRequired,
  updateFilter: Pt.func.isRequired,
  working: Pt.bool.isRequired,
}



// WEBPACK FOOTER //
// ./src/components/Signup/MspSignup.jsx